/**
 * @file store.js
 * @description Configures the Redux store with all feature slices and integrates session-based state persistence.
 *              Uses Redux Toolkit's `configureStore` for simplified setup. Persists selected slices to sessionStorage.
 * @author govind dindewad
 * @date 27 January 2025
 */

import { configureStore } from '@reduxjs/toolkit';

// Feature reducers
import commonReducer from './commonSlice';
import userReducer from "../features/users/userSlice";
import platformsReducer from "../features/platforms/platformsSlice";
import plansReducer from "../features/plans/planSlice";
import poolsReducer from "../features/pools/poolsSlice";
import poolVerificationReducer from "../features/poolVerifivation/poolVerificationSlice";
import complaintReducer from "../features/complaints/complaintsSlice";
import feedbackReducer from "../features/feedback/feedbackSlice";
import notificationReducer from "../features/notifications/notificationSlice";
import revenueReducer from "../features/revenue/revenueSlice.js";
import userPaymentReducer from "../features/userPayments/userPaymentSlice.js"

// State persistence helpers
import { saveState, loadState } from '../utils/localStorage';

// Load previously saved state (from sessionStorage)
const persistedState = loadState();

/**
 * @constant store
 * @description Redux store instance. Combines feature reducers and loads preloaded state if available.
 */
const store = configureStore({
    reducer: {
        common: commonReducer,
        user: userReducer,
        platforms: platformsReducer,
        plans: plansReducer,
        pools: poolsReducer,
        poolVerification: poolVerificationReducer,
        complaint: complaintReducer,
        feedback: feedbackReducer,
        notification: notificationReducer,
        revenue: revenueReducer,
        userPayment:userPaymentReducer
    },
    preloadedState: persistedState, // sets initial state
});

/**
 * Subscribes to store updates and saves specific slices to sessionStorage.
 * This ensures persistence within the session for selected parts of the state.
 */
store.subscribe(() => {
    saveState({
        common: store.getState().common,
        complaint: store.getState().complaint,
        platforms: store.getState().platforms,
        plans: store.getState().plans,
        pools: store.getState().pools,
        user: store.getState().user,
        notification: store.getState().notification,
        revenue: store.getState().revenue,
        poolVerification: store.getState().poolVerification,
        userPayment:store.getState().userPayment
    });
});
export default store;


